import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "./Logo";
import FeaturedRotator from "./FeaturedRotator";
import MegaSearch from "./MegaSearch";
import { NAV, MEGA } from "../data/site";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const EASE = [0.22, 1, 0.36, 1];

export default function Navbar() {
    const [open, setOpen] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const closeTimer = useRef(null);

    const cancelClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = null;
    };
    const scheduleClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => setOpen(null), 160);
    };

    useEffect(() => {
        setOpen(null);
        setMobileOpen(false);
    }, [location.pathname, location.search, location.hash]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                setOpen(null);
                setMobileOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        document.documentElement.style.overflow = mobileOpen ? "hidden" : "";
        return () => {
            document.documentElement.style.overflow = "";
        };
    }, [mobileOpen]);

    const headerItems = NAV.filter((i) => i.header);

    const cls = ({ isActive }) =>
        `py-2 font-display text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors hover:text-blue ${
            isActive ? "text-blue" : "text-ink"
        }`;

    const mega = open ? MEGA[open] : null;

    return (
        <>
            <header
                className="fixed inset-x-0 top-0 z-50 border-b border-line bg-paper/95 backdrop-blur-md"
                data-testid="site-header"
                onMouseLeave={scheduleClose}
            >
                <div className="container-x flex h-20 items-center justify-between">
                    <Link to="/" aria-label="AddiGen home" data-testid="nav-logo">
                        <Logo />
                    </Link>
                    <nav aria-label="Primary" className="hidden lg:block">
                        <ul className="flex items-center gap-7" onMouseEnter={cancelClose}>
                            {headerItems.map((item) =>
                                item.mega ? (
                                    <li key={item.label}>
                                        <button
                                            aria-expanded={open === item.mega}
                                            aria-haspopup="true"
                                            data-testid={`nav-link-${slug(item.label)}`}
                                            onMouseEnter={() => {
                                                cancelClose();
                                                setOpen(item.mega);
                                            }}
                                            onClick={() => setOpen(open === item.mega ? null : item.mega)}
                                            className={`flex items-center gap-2 py-2 font-display text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors hover:text-blue ${
                                                open === item.mega ? "text-blue" : "text-ink"
                                            }`}
                                        >
                                            {item.label}
                                            <svg
                                                viewBox="0 0 10 6"
                                                className={`h-1.5 w-2.5 transition-transform duration-300 ${open === item.mega ? "rotate-180" : ""}`}
                                                aria-hidden="true"
                                            >
                                                <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.4" />
                                            </svg>
                                        </button>
                                    </li>
                                ) : (
                                    <li key={item.label}>
                                        <NavLink
                                            to={item.path}
                                            onMouseEnter={() => setOpen(null)}
                                            className={cls}
                                            data-testid={`nav-link-${slug(item.label)}`}
                                        >
                                            {item.label}
                                        </NavLink>
                                    </li>
                                )
                            )}
                            <li>
                                <Link to="/contact" className="btn-primary !px-5 !py-2.5 !text-[11px]" data-testid="nav-contact-cta">
                                    Contact
                                </Link>
                            </li>
                        </ul>
                    </nav>
                    <button
                        className="relative z-[60] flex h-11 w-11 flex-col items-center justify-center gap-1.5 border border-line-2 lg:hidden"
                        aria-expanded={mobileOpen}
                        aria-label={mobileOpen ? "Close menu" : "Open menu"}
                        data-testid="mobile-menu-button"
                        onClick={() => setMobileOpen(!mobileOpen)}
                    >
                        <span className={`block h-px w-5 bg-ink transition-transform duration-300 ${mobileOpen ? "translate-y-[3.5px] rotate-45" : ""}`} />
                        <span className={`block h-px w-5 bg-ink transition-transform duration-300 ${mobileOpen ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {mega && (
                    <motion.div
                        key={open}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25, ease: EASE }}
                        className="fixed inset-x-0 top-20 z-40 hidden border-b border-line bg-paper shadow-[0_40px_80px_rgba(10,24,40,0.10)] lg:block"
                        data-testid={`mega-panel-${open}`}
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                    >
                        <div className="container-x grid grid-cols-12 gap-8 py-12">
                            <div className="col-span-4 pr-6">
                                <p className="eyebrow mb-5">{mega.eyebrow}</p>
                                <p className="font-display text-2xl font-semibold leading-snug tracking-[-0.01em] text-ink">
                                    {mega.title}
                                </p>
                                <p className="body-sm mt-4 max-w-xs">{mega.desc}</p>
                                <MegaSearch onNavigate={() => setOpen(null)} />
                            </div>
                            <div className="col-span-5">
                                <ul className="border-t border-line">
                                    {mega.links.map((l) => (
                                        <li key={l.label} className="border-b border-line">
                                            <Link
                                                to={l.path}
                                                onClick={() => setOpen(null)}
                                                className="group flex items-center justify-between gap-6 py-4"
                                                data-testid={`mega-link-${slug(l.label)}`}
                                            >
                                                <span>
                                                    <span className="block font-display text-base font-semibold text-ink transition-colors group-hover:text-blue">
                                                        {l.label}
                                                    </span>
                                                    <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-ink-3">
                                                        {l.desc}
                                                    </span>
                                                </span>
                                                <span aria-hidden="true" className="font-mono text-sm text-ink-3 transition-transform duration-300 group-hover:translate-x-1">
                                                    →
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="col-span-3">
                                <FeaturedRotator items={mega.featuredList} onNavigate={() => setOpen(null)} />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {mega && (
                    <motion.div
                        key="mega-backdrop"
                        data-testid="mega-backdrop"
                        className="fixed inset-0 z-30 hidden bg-navy/35 lg:block"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => setOpen(null)}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 top-20 z-40 overflow-y-auto bg-navy px-6 pb-16 pt-8 lg:hidden"
                        data-testid="mobile-menu"
                        data-lenis-prevent
                    >
                        <ul className="space-y-1">
                            {NAV.map((item) =>
                                item.children ? (
                                    <li key={item.label}>
                                        <p className="border-b border-line-navy py-4 font-display text-2xl font-bold text-white">{item.label}</p>
                                        <ul className="py-2">
                                            {item.children.map((c) => (
                                                <li key={c.path}>
                                                    <Link to={c.path} className="block py-2.5 pl-4 text-[15px] text-white/70 transition-colors hover:text-blue-bright" data-testid="mobile-company-link">
                                                        {c.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ) : (
                                    <li key={item.label}>
                                        <Link to={item.path} className="block border-b border-line-navy py-4 font-display text-2xl font-bold text-white transition-colors hover:text-blue-bright" data-testid={`mobile-link-${slug(item.label)}`}>
                                            {item.label}
                                        </Link>
                                    </li>
                                )
                            )}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
